import ReactDOM from 'react-dom/client';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import '@fontsource-variable/archivo';
import '@fontsource-variable/inter';

import { store, persistor } from './redux/store';

import { Login } from './pages/Login';
import { UrlAuth } from './pages/UrlAuth';
import { Home } from './pages/Home';
import { Automations } from './pages/Automations';
import Flow from './pages/Automations/pages/Flow/Flow';
import { NavigationProvider } from './providers/NavigationProvider';
import { AuthGuard } from './components/AuthGuard';
import { Tasks } from './pages/Tasks';
import { OrgConfig, AliareFieldMappingContainer } from './pages/AliareConfig';
import { Canonicals } from './pages/Canonicals';
import { Profile } from './pages/Profile';
import { Layout } from './components/Layout';
import { SnackbarProvider } from 'notistack';
import { Scripts } from './pages/Scripts';
import { ErrorProvider } from './contexts/ErrorContext';
import { Runs } from './pages/Runs';

type PathRoutes = 'automations' | 'tasks' | 'canonicals' | 'profile' | 'scripts' | 'org-config' | 'runs';
const fromPathRouteToComponent: Record<PathRoutes, React.FC> = {
  automations: Automations,
  tasks: Tasks,
  canonicals: Canonicals,
  profile: Profile,
  scripts: Scripts,
  'org-config': OrgConfig,
  runs: Runs,
};
const availablePathRoutes: PathRoutes[] = Object.keys(fromPathRouteToComponent) as PathRoutes[];

export default function App() {
  const DynamicRoute: React.FC = () => {
    const { path } = useParams<{ path: PathRoutes }>();

    if (!path || !availablePathRoutes.includes(path)) {
      return <Navigate to={'/'} replace />;
    }

    const Component = fromPathRouteToComponent[path];
    return <Component />;
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: 300000,
      },
    },
  });

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <SnackbarProvider maxSnack={3}>
                <NavigationProvider>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/url/auth" element={<UrlAuth />} />

                    <Route
                      element={
                        <AuthGuard>
                          <Layout />
                        </AuthGuard>
                      }
                    >
                      <Route path="/" element={<Home />} />
                      <Route path="/:path" element={<DynamicRoute />} />
                    </Route>

                    <Route
                      path="/org-config/aliare-mapping"
                      element={
                        <AuthGuard>
                          <AliareFieldMappingContainer />
                        </AuthGuard>
                      }
                    />

                    <Route
                      path="/automations/flows"
                      element={
                        <AuthGuard>
                          <Flow />
                        </AuthGuard>
                      }
                    />

                    <Route path="/*" element={<Navigate to={'/'} replace />} />
                  </Routes>
                </NavigationProvider>
              </SnackbarProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ErrorProvider>
      </PersistGate>
    </Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);
